import { CLEAR_OUTPUT, RESET_OUTPUT } from "../../config/constants";
import useCommandFlowStore from "../../store/commandFlowStore";
import { getTranslationFunction } from "../../utils/core/translationUtils";
import {
  parseMarkedText,
  stripMarkers,
} from "../../utils/ui/outputProcessingUtils";

// Handles CLEAR_OUTPUT using setters
export function handleClearOutputWithSetters({
  result,
  setters,
  engine,
  onCommandComplete,
}) {
  if (result.output === CLEAR_OUTPUT) {
    const setOutput = useCommandFlowStore.getState().setOutput;
    const setWasCleared = useCommandFlowStore.getState().setWasCleared;
    const setInSession = useCommandFlowStore.getState().setInSession;
    setOutput([]);
    setWasCleared(true); // Mark that output was intentionally cleared
    setters.setCurrentPrompt(null);
    setInSession(false);
    // Reset typing/processing flags so the prompt input reappears
    useCommandFlowStore.getState().setIsTyping(false);
    useCommandFlowStore.getState().setIsAwaitingPrompt(false);
    useCommandFlowStore.getState().setIsSkippableDelay(false);
    useCommandFlowStore.getState().setIsLoading(false);
    if (onCommandComplete) {
      const commandName = engine.currentCommandName;
      onCommandComplete(commandName);
      // Clear currentCommandName after callback is invoked
      engine.sessionManager.currentCommandName = null;
    }
    return true;
  }
  return false;
}

// Handles RESET_OUTPUT using setters
export function handleResetOutputWithSetters({
  result,
  setters,
  initMessage,
  engine,
  onCommandComplete,
}) {
  if (result.output === RESET_OUTPUT) {
    const setOutput = useCommandFlowStore.getState().setOutput;
    const setInSession = useCommandFlowStore.getState().setInSession;
    setOutput(initMessage);
    setters.setCurrentPrompt(null);
    setInSession(false);
    setters.setResetToken((prev) => prev + 1);
    if (onCommandComplete) {
      const commandName = engine.currentCommandName;
      const sessionData =
        engine.sessionManager.steplessSessionData ||
        engine.sessionManager.session?.data ||
        {};
      onCommandComplete(commandName, sessionData);
      // Clear currentCommandName and steplessSessionData after callback is invoked
      engine.sessionManager.currentCommandName = null;
      engine.sessionManager.steplessSessionData = null;
    }
    return true;
  }
  return false;
}

// Handles result output with error suggestions
export function handleResultOutput({ result, commands, trimmed }) {
  if (result.error) {
    const setOutput = useCommandFlowStore.getState().setOutput;
    const t = getTranslationFunction();
    const suggestions = Object.entries(commands)
      .filter(([name, cmd]) => !cmd.hidden)
      .map(([name]) => name)
      .filter((name) => name.includes(trimmed) || name.startsWith(trimmed[0]));

    if (suggestions.length) {
      setOutput((prev) => [
        ...prev,
        `${result.error}`,
        t("didYouMean", {
          suggestions: suggestions.join(", "),
        }),
      ]);
    } else {
      setOutput((prev) => [...prev, result.error]);
    }
    return true;
  }
  return false;
}

// Handles starting a new command with simplified logic
export async function handleStartNewCommand({
  trimmed,
  engine,
  resolvePrompt,
  setCurrentPrompt,
  onStepStart,
  onCommandStart,
}) {
  const promptLabel = useCommandFlowStore.getState().promptLabel;
  const setOutput = useCommandFlowStore.getState().setOutput;
  const setIsLoading = useCommandFlowStore.getState().setIsLoading;
  const setInSession = useCommandFlowStore.getState().setInSession;

  // Handle prompt display
  if (promptLabel) {
    const promptEndsWithNewline = promptLabel.trim().endsWith("\n");
    if (promptEndsWithNewline) {
      setOutput((prev) => [
        ...prev,
        { type: "input", text: promptLabel.trimEnd() },
        { type: "input", text: trimmed },
      ]);
    } else {
      setOutput((prev) => [
        ...prev,
        {
          type: "output",
          text: [
            { text: promptLabel, color: "output" },
            { text: " ", color: null },
            { text: trimmed, color: "input" },
          ],
        },
      ]);
    }
  } else {
    setOutput((prev) => [...prev, { type: "input", text: trimmed }]);
  }

  setIsLoading(true);

  // Check for initial data from button triggers
  let initialData = null;
  if (
    window._commandFlowInitialData &&
    Date.now() - window._commandFlowInitialData.timestamp < 5000
  ) {
    // 5 second timeout
    initialData = window._commandFlowInitialData.data;
    // Clear the temporary data
    delete window._commandFlowInitialData;
  }

  const result = await engine.startCommand(trimmed, initialData);

  // Fire onCommandStart IMMEDIATELY after the engine recognizes the command,
  // BEFORE the prompt-typing animation. The user's intent ("I started this
  // command") happens at the moment they hit Enter — consumers shouldn't
  // wait for the typing effect to finish before being notified. This is
  // the latency-sensitive moment for verification engines.
  //
  // Only fire when a session is actually about to open (i.e. there is a
  // prompt). Commands without prompts (like `help`) complete synchronously
  // and don't open a session, so onCommandStart shouldn't fire for them.
  if (result.prompt && onCommandStart) {
    try {
      const sessionDataSnapshot =
        useCommandFlowStore.getState().sessionData ?? {};
      onCommandStart(engine.currentCommandName, sessionDataSnapshot);
    } catch (err) {
      console.error("onCommandStart threw:", err);
    }
  }

  // Only clear isLoading when there's no prompt coming. If there IS a prompt,
  // the prompt typing effect will clear isLoading — clearing it here would
  // briefly flash the ">" default prompt before the new prompt starts typing.
  if (!result.prompt) {
    setIsLoading(false);
  }

  if (result.prompt) {
    const resolvedPrompt = await resolvePrompt(result.prompt);

    // Check if prompt contains multiple lines and split them for delayed display
    if (typeof resolvedPrompt === "string" && resolvedPrompt.includes("\n")) {
      const addOutputWithTypingEffect =
        useCommandFlowStore.getState().addOutputWithTypingEffect;
      const lines = resolvedPrompt.split("\n");
      const lastLine = lines.pop(); // Remove the last line to use as prompt

      if (lines.length > 0) {
        // Join into a single output entry so one Enter skips the whole block.
        // CSS `white-space: pre-wrap` renders the embedded \n as line breaks.
        const outputLines = [
          { type: "output", text: parseMarkedText(lines.join("\n")) },
        ];
        await addOutputWithTypingEffect(outputLines);
        // Re-set isLoading so the ">" doesn't flash before the prompt typing starts
        useCommandFlowStore.setState({ isLoading: true });
      }

      // Set only the last line as the current prompt with typing effect
      const setCurrentPromptWithTypingEffect =
        useCommandFlowStore.getState().setCurrentPromptWithTypingEffect;
      await setCurrentPromptWithTypingEffect(stripMarkers(lastLine));
    } else {
      // Single-line prompt - use typing effect
      const setCurrentPromptWithTypingEffect =
        useCommandFlowStore.getState().setCurrentPromptWithTypingEffect;
      await setCurrentPromptWithTypingEffect(stripMarkers(resolvedPrompt));
    }

    setInSession(true);

    // (onCommandStart already fired above, immediately after engine.startCommand
    // returned, so verification doesn't wait for the typing animation.)

    if (onStepStart) {
      onStepStart(engine.currentCommandName);
    }
    return { handled: true, result };
  }
  return { handled: false, result };
}
